import React, { useState, useEffect, useCallback } from "react";
import {
  ReactFlow,
  useReactFlow,
  useNodesState,
  useEdgesState,
} from "reactflow";

import { useAutoLayoutContext } from "../../contexts/AutoLayoutProvider";
import { useFlowElementsContext } from "../../contexts/FlowElementsProvider";
import { createGraphLayout } from "../../util/graphUtils";

import CustomControls from "./CustomControls";
import Spinner from "./common/Spinner";
import FadeInOut from "../FadeInOut";

import "../../reactflow.css";

const FlowComponent = ({ graph }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(graph.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(graph.edges);
  const [isLoading, setIsLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);

  const { autoLayoutOn } = useAutoLayoutContext();
  const { nodeTypes, edgeTypes } = useFlowElementsContext();

  const proOptions = { hideAttribution: true };
  const fitViewOptions = {
    duration: 750,
  };
  const { fitView, getNodes, getEdges } = useReactFlow();

  const autoLayout = async () => {
    const loadedNodes = await getNodes();
    const loadedEdges = await getEdges();
    const { layoutedNodes, layoutedEdges } = await createGraphLayout(
      loadedNodes,
      loadedEdges
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  };

  useEffect(() => {
    const id = setTimeout(() => setIsMoving(false), 420);
    return () => clearTimeout(id);
  }, [isMoving]);

  const handleInit = () => {
    setIsMoving(true);
    autoLayout();
    setTimeout(() => {
      fitView();
      setIsLoading(false);
    }, 100);
  };

  const onNodesChangeModifier = () => {
    setIsMoving(true);
    autoLayout();
  };

  const onGraphChange = useCallback(() => {
    setIsLoading(true);

    const { layoutedNodes, layoutedEdges } = createGraphLayout(
      graph.nodes,
      graph.edges
    );

    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  useEffect(() => {
    if (!isLoading) {
      onGraphChange();
      setTimeout(() => {
        fitView(fitViewOptions);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  return (
    <>
      <FadeInOut
        style={{
          width: "100vw",
          height: "100vh",
          position: "absolute",
          zIndex: 99999,
        }}
        show={isLoading}
        duration={200}
      >
        <Spinner />
      </FadeInOut>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={
          autoLayoutOn
            ? !isMoving
              ? onNodesChangeModifier
              : autoLayout
            : onNodesChange
        }
        onEdgesChange={onEdgesChange}
        onInit={handleInit}
        className={isLoading ? "react-flow__loading" : ""}
        // ----- SETTINGS -----
        snapToGrid
        snapGrid={[10, 10]}
        minZoom={0}
        proOptions={proOptions}
      >
        <CustomControls fitViewOptions={fitViewOptions} />
      </ReactFlow>
    </>
  );
};

export default FlowComponent;
